import capitalize from 'lodash/capitalize';
import words from 'lodash/words';
import { BreadcrumbJsonLd } from 'next-seo';
import { useEffect, useState } from 'react';
import type { UrlObject } from 'url';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import type { ContainerProps } from '../Container';
import LinkWithLocale from '../LinkWithLocale/LinkWithLocale';
import { BreadcrumbChevron } from './BreadcrumbChevron';

import * as S from './Breadcrumb.styles';

export interface ExpanderProps {
  onClick: () => void;
}

const Expander = ({ onClick }: ExpanderProps) => (
  <S.ExpanderWrapper data-test-id='Expander' onClick={onClick}>
    <S.Dot data-test-id='ExpanderDot' />
    <S.Dot data-test-id='ExpanderDot' />
    <S.Dot data-test-id='ExpanderDot' />
  </S.ExpanderWrapper>
);

export interface BreadcrumbItem {
  label: string;
  target: UrlObject | string;
}

export interface BreadcrumbProps {
  inverted?: boolean;
  isProductDetailsPage?: boolean;
  items: BreadcrumbItem[];
}
const customStartCase = (str: string) => {
  return words(str, /[^ ]+/g)
    .map((word) => (word === word.toUpperCase() ? word : capitalize(word)))
    .join(' ');
};

export const Breadcrumb = ({
  inverted = false,
  isProductDetailsPage = false,
  items,
}: BreadcrumbProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (items.length < 4) {
      setIsExpanded(true);
    }
  }, [items]);

  const jsonLdItemList = items.map((item, index) => ({
    position: index + 1,
    name: customStartCase(item.label),
    item: typeof item.target === 'object' ? item.target.pathname : item.target,
  }));
  if (isExpanded) {
    return (
      <>
        <S.Wrapper data-test-id='BreadcrumbWrapper'>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <S.ItemWrapper
                key={item.label}
                data-test-id={`ItemWrapper-${index}`}
                inverted={inverted}
              >
                {index !== 0 && (
                  <BreadcrumbChevron
                    data-test-id={`BreadcrumbChevron-${index}`}
                  />
                )}
                {isLast && !isProductDetailsPage ? (
                  <span data-test-id='BreadcrumbLabel'>
                    {customStartCase(item.label)}
                  </span>
                ) : (
                  <LinkWithLocale
                    aria-label={getLinkAriaLabel(item.target, item.label)}
                    data-test-id={`BreadcrumbLink-${index}`}
                    href={item.target}
                  >
                    {customStartCase(item.label)}
                  </LinkWithLocale>
                )}
              </S.ItemWrapper>
            );
          })}
        </S.Wrapper>
        <BreadcrumbJsonLd itemListElements={jsonLdItemList} />
      </>
    );
  }

  return (
    <>
      <S.Wrapper data-test-id='BreadcrumbWrapper'>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isFirst = index === 0;

          return isFirst || isLast ? (
            <S.ItemWrapper
              key={item.label}
              data-test-id={`ItemWrapper-${index}`}
              inverted={inverted}
            >
              {isLast && !isProductDetailsPage ? (
                <span data-test-id='BreadcrumbLabel'>
                  {customStartCase(item.label)}
                </span>
              ) : (
                <LinkWithLocale
                  aria-label={(getLinkAriaLabel(item.target), item.label)}
                  data-test-id={`BreadcrumbLink-${index}`}
                  href={item.target}
                >
                  {customStartCase(item.label)}
                </LinkWithLocale>
              )}
              {!isLast && (
                <BreadcrumbChevron
                  data-test-id={`BreadcrumbChevron-${index}`}
                />
              )}
              {isFirst && (
                <Expander
                  data-test-id='Expander'
                  onClick={() => setIsExpanded(!isExpanded)}
                />
              )}
            </S.ItemWrapper>
          ) : null;
        })}
      </S.Wrapper>
      <BreadcrumbJsonLd itemListElements={jsonLdItemList} />
    </>
  );
};

export const Wrapper = ({ children, ...props }: ContainerProps) => {
  return <S.BreadcrumbWrapper {...props}>{children}</S.BreadcrumbWrapper>;
};

Breadcrumb.Wrapper = Wrapper;

Breadcrumb.displayName = 'Breadcrumb';
